import Typography from '@material-ui/core/Typography';
import FilterCard from 'Components/Editor/Details/Filters/FilterCard';
import FilterSlider from 'Components/Editor/Details/Filters/FilterSlider';
import { DetectionTimeSpecification } from 'Models/Project/ProjectFilterSpecifications';
import { ReactElement, useEffect, useState } from 'react';
import { padNumber } from 'Utils/conversions';

interface FilterCardTimeProps {
	time: DetectionTimeSpecification | null;
	onTimeChanged: (time: DetectionTimeSpecification | null) => void;
	video: HTMLVideoElement | null;
}

const INITIAL_TIME_VALUE: DetectionTimeSpecification = { from: 0, to: 0 };

const FilterCardTime = (props: FilterCardTimeProps): ReactElement => {
	const { video } = props;
	const [videoDuration, setVideoDuration] = useState(video?.duration ?? NaN);
	const hasVideoDuration = Number.isFinite(videoDuration) && videoDuration > 0;
	const isChecked = props.time !== null;
	const fromValue = Math.round((props.time?.from ?? 0) / 1000);
	const toValue = Math.round((props.time?.to ?? 0) / 1000);

	useEffect(() => {
		const updateDuration = (): void => setVideoDuration(video?.duration ?? NaN);
		updateDuration();
		video?.addEventListener('loadedmetadata', updateDuration);
		video?.addEventListener('durationchange', updateDuration);
		return () => {
			video?.removeEventListener('loadedmetadata', updateDuration);
			video?.removeEventListener('durationchange', updateDuration);
		};
	}, [video]);

	const getValueLabelFormat = (value: number): string => {
		const seconds = padNumber(Math.floor(value % 60), 2);
		const minutes = padNumber(Math.floor(value / 60) % 60, 2);
		const hours = padNumber(Math.floor(value / 3600), 2);
		return `${hours}:${minutes}:${seconds}`;
	};

	const startMark = {
		value: 0,
		label: getValueLabelFormat(0),
	};
	const middleMark = {
		value: Math.floor(videoDuration / 2),
		label: getValueLabelFormat(videoDuration / 2),
	};
	const endMark = {
		value: Math.floor(videoDuration),
		label: getValueLabelFormat(videoDuration),
	};
	const marks = [startMark, middleMark, endMark];

	const handlePlacementCardCheckedChanged = (checked: boolean): void => {
		if (checked) props.onTimeChanged(INITIAL_TIME_VALUE);
		else props.onTimeChanged(null);
	};

	const setValue = (from: number, to: number): void => {
		props.onTimeChanged({ from: from * 1000, to: to * 1000 });
	};

	return (
		<FilterCard name="Select time" onCheckedChanged={handlePlacementCardCheckedChanged} isChecked={isChecked}>
			{hasVideoDuration ? (
				<FilterSlider
					label="Time"
					fromValue={fromValue}
					toValue={toValue}
					min={0}
					max={videoDuration}
					marks={marks}
					valueLabelFormat={getValueLabelFormat}
					onValuesChanged={setValue}
					type="time"
				/>
			) : (
				<Typography>Video duration is loading.</Typography>
			)}
		</FilterCard>
	);
};

export default FilterCardTime;
